import React, { useEffect, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { Tooltip, Button } from 'antd'
import { get, keyBy } from 'lodash'
import { connect } from 'react-redux'
import { compose } from 'redux'
import {
  FlexContainer,
  AnswerContext,
  helpers,
  CheckboxLabel,
  NumberInputStyled,
} from '@edulastic/common'
import UnScored from '@edulastic/common/src/components/Unscored'
import { IconCheck, IconPencilEdit } from '@edulastic/icons'
import { greenPrimary } from '@edulastic/colors'
import { segmentApi } from '@edulastic/api'
import { withRouter } from 'react-router-dom'
import { roleuser } from '@edulastic/constants'
import TestItemPreview from '../../../../../../../assessment/components/TestItemPreview'
import { PointsLabel, AIReviewContainer } from './styled'
import { ScoreInputWrapper, SaveToApply, InfoIcon } from '../styled'
import Actions from '../Actions'
import {
  getCollectionsSelector,
  getUserFeatures,
  getUserRole,
  getWritableCollectionsSelector,
} from '../../../../../../src/selectors/user'
import {
  changePreviewAction,
  changeViewAction,
} from '../../../../../../src/actions/view'
import {
  reviewAiGeneratedRequestAction,
  getTestSelector,
  updateTestAndNavigateAction,
} from '../../../../../ducks'
import { clearItemDetailAction } from '../../../../../../ItemDetail/ducks'
import { getAssignmentsSelector } from '../../../../../../AssignTest/duck'
import {
  editNonAuthoredItemAction,
  verifyItemUsedInTestAndProceedAction,
} from '../../../../../../src/components/common/PreviewModal/ducks'
import {
  allowContentEditCheck,
  isContentOfCollectionEditable,
  allowDuplicateCheck,
} from '../../../../../../src/utils/permissionCheck'

const transformItemRow = (row, qid) => [
  {
    ...row,
    widgets: row.widgets.filter((x) => {
      if (x.widgetType === 'question') {
        return x.reference === qid
      }
      return false
    }),
  },
]

const splitItems = (item, testItem) => {
  const questionRow = item.find((x) =>
    x.widgets.some((w) => w.widgetType === 'question')
  )
  return testItem.data?.questions.map(({ id }) => ({
    item: transformItemRow(questionRow, id),
  }))
}

function AIReviewButtons_({
  testId,
  itemId,
  reviewRequestInProgress,
  markAiGeneratedAnswerReviewed,
  reviewNeeded,
  item,
  test,
  userId,
  collections,
  writableCollections,
  changeView,
  clearItemStore,
  userRole,
  userFeatures,
  verifyItemUsedInTestAndProceed,
  editNonAuthoredItem,
  updateTestAndNavigate,
  history,
  testAssignments,
  allPassages,
}) {
  const domRef = useRef(null)
  /**
   * Bringing the button into view when the component is mounted
   */
  useEffect(() => {
    if (domRef.current) {
      setTimeout(() => {
        domRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 1000)
    }
  }, [])

  const launchEditItem = () => {
    /* preserving the logic from preview modal edit test item button
     https://github.com/snapwiz/edulastic-poc/blob/520c2a4cc7bb6e87bbfe7fd22f9d50e80f8b8847/src/client/author/src/components/common/PreviewModal/PreviewModal.js#L498
     Unfortunately, we cannot use the same code here because the button is not inside the preview modal.
     */

    const page = 'review'
    const isTest = true
    segmentApi.genericEventTrack('TestReview:AI:EditAnswer', {
      aiDocExtracted: true,
      aiGenerated: true,
    })

    const regradeFlow = !!test?._id && testAssignments.length && test.isUsed
    const isOwner = item?.authors?.some((author) => author._id === userId)
    const hasCollectionAccess = allowContentEditCheck(
      item?.collections,
      writableCollections
    )
    const isCollectionContentEditable = isContentOfCollectionEditable(
      item?.collections,
      writableCollections
    )

    const allowDuplicate = allowDuplicateCheck(
      item?.collections,
      collections,
      'item'
    )

    const isDisableEdit = !(
      isOwner ||
      userRole === roleuser.EDULASTIC_CURATOR ||
      (hasCollectionAccess && userFeatures.isCurator) ||
      isCollectionContentEditable
    )

    // change the question editor view to "edit"
    changeView('edit')
    // itemDetail store has leftovers from previous visit to the page,
    // clearing it before navigation.

    clearItemStore()

    const isCuratorUser =
      userFeatures?.isCurator || userRole === roleuser.EDULASTIC_CURATOR

    if (
      (item.status === 'draft' || item.passageId) &&
      page === 'review' &&
      !isCuratorUser &&
      !isDisableEdit &&
      !regradeFlow
    ) {
      verifyItemUsedInTestAndProceed({
        itemId,
        testId,
        regradeFlow,
        passageId: item.passageId,
      })
      return
    }

    if (isDisableEdit && isTest && allowDuplicate) {
      const passage = allPassages.find((p) => p._id === item.passageId)

      let passageItems = passage && passage.testItems
      const passageId = passage && passage._id
      if (passageItems && passageId) {
        const testItemIds = test.itemGroups.flatMap((group) =>
          group.items.map((ele) => ele._id)
        )
        passageItems = passageItems.filter((ele) => testItemIds.includes(ele))
      }

      return editNonAuthoredItem({
        itemId,
        testId,
        replaceOldItem: true,
        passageItems,
        passageId,
        regradeFlow,
      })
    }
    if (isTest) {
      const payload = {
        pathname: `/author/tests/${testId}/editItem/${itemId}`,
        fadeSidebar: 'false',
        regradeFlow,
        testId,
        isEditing: true,
        isSectionAdaptiveTest: false,
      }
      if (testId !== test.previousTestId) {
        payload.previousTestId = test.previousTestId
      }
      updateTestAndNavigate(payload)
    } else {
      history.push(`/author/items/${itemId}/item-detail`)
    }
  }

  return (
    <AIReviewContainer
      onClick={(e) => {
        e.stopPropagation()
      }}
      ref={domRef}
      data-cy={reviewNeeded ? 'aiAnswerReviewNeeded' : 'aiAnswerReviewed'}
    >
      {reviewNeeded ? (
        <>Review answer generated :</>
      ) : (
        <>
          Answer reviewed{' '}
          <IconCheck style={{ marginLeft: 5 }} color={greenPrimary} />
        </>
      )}

      {reviewNeeded && (
        <Button
          type="link"
          ghost
          loading={reviewRequestInProgress}
          data-cy="markAsReviewedButton"
          onClick={() => markAiGeneratedAnswerReviewed({ testId, itemId })}
        >
          {reviewRequestInProgress ? null : <IconCheck color={greenPrimary} />}{' '}
          {reviewRequestInProgress ? 'Marking...' : 'Mark As Reviewed'}
        </Button>
      )}
      {reviewNeeded && (
        <Button
          type="link"
          ghost
          onClick={launchEditItem}
          data-cy="editAnswerButton"
        >
          <IconPencilEdit color={greenPrimary} /> Edit Answer
        </Button>
      )}
    </AIReviewContainer>
  )
}
// compose with withRouter & compose with connect
const AIReviewButtons = compose(
  withRouter,
  connect(
    (state, ownProps) => ({
      reviewRequestInProgress:
        state.tests.aiGeneratedReviewInProgress?.[ownProps.itemId] || false,
      userId: state?.user?.user?._id,
      userRole: getUserRole(state),
      userFeatures: getUserFeatures(state),
      test: getTestSelector(state),
      collections: getCollectionsSelector(state),
      writableCollections: getWritableCollectionsSelector(state),
      testAssignments: getAssignmentsSelector(state),
      allPassages: state.tests?.entity?.passages || [],
    }),
    {
      markAiGeneratedAnswerReviewed: reviewAiGeneratedRequestAction,
      changeView: changeViewAction,
      changePreviewMode: changePreviewAction,
      clearItemStore: clearItemDetailAction,
      verifyItemUsedInTestAndProceed: verifyItemUsedInTestAndProceedAction,
      editNonAuthoredItem: editNonAuthoredItemAction,
      updateTestAndNavigate: updateTestAndNavigateAction,
    }
  )
)(AIReviewButtons_)

const Expanded = ({
  item,
  testItem,
  isEditable = false,
  onChangePoints,
  owner,
  metaInfoData,
  onPreview,
  handleAiOptionSelected,
  questions,
  passagesKeyed,
  mobile,
  isScoringDisabled = false,
  scoring,
  onSelect,
  onDelete,
  collapsRow,
  checked,
  points: pointsProp,
  groupPoints,
  groupMinimized,
  isUnScoredItem,
  showAltScoreInfo,
  isPremiumContentWithoutAccess,
  premiumCollectionWithoutAccess,
  isTestsUpdated,
  setShowAutoSelectScoreChangeModal,
  showScorePopUp,
  isEditDisabledForSectionsAdaptiveTest,
  testId,
}) => {
  const [scoreChanged, setScoreChanged] = useState(false)

  const showAiActionButton =
    testItem?.unsavedItem && !testItem?.aiDocExtracted && testItem.aiGenerated

  const itemId = testItem._id

  const reviewNeeded = testItem?.aiGeneratedAnswerReviewed === false
  const showAIReviewButtons = 'aiGeneratedAnswerReviewed' in testItem

  const reviewButtons = showAIReviewButtons ? (
    <AIReviewButtons
      reviewNeeded={reviewNeeded}
      testId={testId}
      itemId={itemId}
      item={testItem}
    />
  ) : null

  useEffect(() => {
    if (!isTestsUpdated) {
      setScoreChanged(isTestsUpdated)
    }
  }, [isTestsUpdated])

  /**
   * @type {{item:Object,question:Object}[]}
   */
  const items = testItem.itemLevelScoring
    ? [{ item }]
    : splitItems(item, testItem)
  let passageContent = {}
  if (testItem.passageId && items?.[0]?.item) {
    items[0].item = [
      passagesKeyed[testItem?.passageId]?.structure,
      ...items[0]?.item,
    ]
    passageContent = keyBy(passagesKeyed[testItem.passageId]?.data, 'id')
  }
  const widgetsWithResource = {
    ...questions,
    ...keyBy(testItem?.data?.resources || [], (r) => `${testItem._id}_${r.id}`),
    ...passageContent,
  }
  let points = 0

  const itemLevelScoring = helpers.getPoints(testItem)
  const questionLevelScoring = helpers.getQuestionLevelScore(
    testItem,
    get(testItem, 'data.questions', []),
    itemLevelScoring,
    get(scoring, testItem._id, 0)
  )

  const testItemQuestions = get(testItem, 'data.questions', [])
  const isPassageWithMultipleQuestions =
    (testItem?.isPassageWithQuestions &&
      testItemQuestions.length > 1 &&
      !testItem?.itemLevelScoring) ||
    false

  if (testItem.itemLevelScoring || mobile) {
    points = get(scoring, testItem._id, itemLevelScoring)
  } else {
    points = get(scoring, `questionLevel.${testItem._id}`, questionLevelScoring)
  }

  const handleChangePoint = (qid) => (value) => {
    const questionScore = value
    if (!testItem.itemLevelScoring && qid) {
      const updatedPoints = { ...points, [qid]: questionScore }
      const itemScore = Object.values(updatedPoints).reduce(
        (acc, curr) => curr + acc
      )
      onChangePoints(metaInfoData.id, itemScore, updatedPoints)
    } else {
      onChangePoints(metaInfoData.id, questionScore)
    }

    setScoreChanged(true)
  }

  return mobile ? (
    <FlexContainer flexDirection="column" alignItems="flex-start">
      <FlexContainer
        justifyContent="space-between"
        style={{ width: '100%', marginBottom: '15px' }}
      >
        {isEditable && (
          <FlexContainer
            style={{ marginTop: 20, width: '5%' }}
            flexDirection="column"
            justifyContent="center"
          >
            <CheckboxLabel checked={checked} onChange={onSelect} />
          </FlexContainer>
        )}
        <FlexContainer>
          <Actions
            style={{ marginBottom: 8, width: 108 }}
            onPreview={() => onPreview(metaInfoData.id)}
            onCollapseExpandRow={collapsRow}
            onDelete={onDelete}
            isEditable={isEditable}
            expanded
            showAiActionButton={showAiActionButton}
            onAiOptionSelected={(aiOption) => {
              handleAiOptionSelected(aiOption, testItem)
            }}
          />
          <FlexContainer flexDirection="column">
            <PointsLabel>Points</PointsLabel>
            <ScoreInputWrapper data-cy="score-input-wrapper">
              <div onClick={setShowAutoSelectScoreChangeModal}>
                {!isUnScoredItem ? (
                  <NumberInputStyled
                    data-cy="pointsd"
                    width="108px"
                    padding="0px 12px"
                    disabled={
                      (!owner ||
                        !isEditable ||
                        isScoringDisabled ||
                        groupMinimized) &&
                      !showScorePopUp
                    }
                    value={groupMinimized ? groupPoints : pointsProp}
                    onChange={(value) =>
                      !showScorePopUp && onChangePoints(metaInfoData.id, value)
                    }
                    textAlign="center"
                  />
                ) : (
                  <UnScored
                    width="60px"
                    height="32px"
                    margin="0px 0px 0px 5px"
                    fontSize="10px"
                    text="Z"
                    fontWeight="700"
                  />
                )}
              </div>
              {showAltScoreInfo && (
                <Tooltip title="Question has alternate answers with different score points.">
                  <InfoIcon />
                </Tooltip>
              )}
              {scoreChanged && <SaveToApply>Save to apply changes</SaveToApply>}
            </ScoreInputWrapper>
          </FlexContainer>
        </FlexContainer>
      </FlexContainer>
      <FlexContainer maxWidth="100%">
        <AnswerContext.Provider
          value={{ isAnswerModifiable: false, hideAnswers: true }}
        >
          <TestItemPreview
            style={{
              marginTop: -10,
              padding: 0,
              boxShadow: 'none',
              display: 'flex',
              maxWidth: '100%',
            }}
            cols={item}
            metaData={metaInfoData.id}
            preview="show"
            verticalDivider={item.verticalDivider}
            disableResponse
            scrolling={item.scrolling}
            questions={questions}
            windowWidth="100%"
            isReviewTab
            testItem
            isPremiumContentWithoutAccess={isPremiumContentWithoutAccess}
            premiumCollectionWithoutAccess={premiumCollectionWithoutAccess}
            itemIdKey={testItem._id}
          />
          {reviewButtons}
        </AnswerContext.Provider>
      </FlexContainer>
    </FlexContainer>
  ) : (
    items &&
      items.map(({ item: _item = [] }, index) => {
        const questionWidgets = _item.filter(({ widgets = [] }) =>
          widgets.find(({ widgetType = '' }) => widgetType === 'question')
        )

        const qId = get(questionWidgets, `[0].widgets[0].reference`, null)

        return (
          <FlexContainer
            className="expanded-rows"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <FlexContainer alignItems="flex-start" style={{ width: '85%' }}>
              {isEditable && (
                <FlexContainer
                  style={{ marginTop: 20, width: '5%' }}
                  flexDirection="column"
                  justifyContent="center"
                >
                  {isEditable && (
                    <CheckboxLabel checked={checked} onChange={onSelect} />
                  )}
                </FlexContainer>
              )}
              <AnswerContext.Provider
                value={{ isAnswerModifiable: false, showAnswers: false }}
              >
                <div
                  onClick={() => onPreview(metaInfoData.id)}
                  style={{ width: '100%', cursor: 'pointer' }}
                >
                  <TestItemPreview
                    style={{
                      padding: 0,
                      boxShadow: 'none',
                      display: 'flex',
                    }}
                    cols={_item}
                    preview="show"
                    metaData={metaInfoData.id}
                    disableResponse
                    verticalDivider={get(_item, '[0].verticalDivider')}
                    scrolling={get(_item, '[0].scrolling')}
                    questions={widgetsWithResource}
                    windowWidth="100%"
                    isReviewTab
                    testItem
                    isPremiumContentWithoutAccess={
                      isPremiumContentWithoutAccess
                    }
                    premiumCollectionWithoutAccess={
                      premiumCollectionWithoutAccess
                    }
                    isPassageWithMultipleQuestions={
                      isPassageWithMultipleQuestions
                    }
                    isExpandedView
                    itemIdKey={testItem._id}
                  />
                  {reviewButtons}
                </div>
              </AnswerContext.Provider>
            </FlexContainer>
            <FlexContainer
              style={{ width: '15%' }}
              flexDirection="column"
              alignItems="flex-end"
            >
              <FlexContainer flexDirection="column" style={{ margin: 0 }}>
                <PointsLabel>Points</PointsLabel>
                <ScoreInputWrapper>
                  <div onClick={setShowAutoSelectScoreChangeModal}>
                    {!isUnScoredItem &&
                    !get(
                      questions,
                      `${testItem._id}_${qId}.validation.unscored`,
                      false
                    ) ? (
                      <NumberInputStyled
                        min={0}
                        width="108px"
                        padding="0px 12px"
                        disabled={
                          (!owner ||
                            !isEditable ||
                            isScoringDisabled ||
                            groupMinimized) &&
                          !showScorePopUp
                        }
                        value={
                          groupMinimized ? groupPoints : points?.[qId] || points
                        }
                        onChange={(v) =>
                          !showScorePopUp && handleChangePoint(qId)(v)
                        }
                        textAlign="center"
                      />
                    ) : (
                      <UnScored
                        width="108px"
                        height="32px"
                        margin="0px 0px 0px 5px"
                        fontSize="10px"
                        text="Z"
                        fontWeight="700"
                      />
                    )}
                  </div>
                  {showAltScoreInfo && (
                    <Tooltip title="Question has alternate answers with different score points.">
                      <InfoIcon />
                    </Tooltip>
                  )}
                  {scoreChanged && (
                    <SaveToApply>Save to apply changes</SaveToApply>
                  )}
                </ScoreInputWrapper>
              </FlexContainer>
              {index === 0 && (
                <Actions
                  style={{ marginTop: 8, width: 108 }}
                  onPreview={() => onPreview(metaInfoData.id)}
                  onCollapseExpandRow={collapsRow}
                  onDelete={onDelete}
                  isEditable={
                    isEditable && !isEditDisabledForSectionsAdaptiveTest
                  }
                  expanded
                  showAiActionButton={showAiActionButton}
                  onAiOptionSelected={(aiOption) => {
                    handleAiOptionSelected(aiOption, testItem)
                  }}
                />
              )}
            </FlexContainer>
          </FlexContainer>
        )
      })
  )
}

Expanded.propTypes = {
  item: PropTypes.object.isRequired,
  testItem: PropTypes.object.isRequired,
  isEditable: PropTypes.bool.isRequired,
  onChangePoints: PropTypes.func.isRequired,
  owner: PropTypes.bool.isRequired,
  metaInfoData: PropTypes.object.isRequired,
  onPreview: PropTypes.func.isRequired,
  questions: PropTypes.object.isRequired,
  passagesKeyed: PropTypes.object.isRequired,
  mobile: PropTypes.bool.isRequired,
  isScoringDisabled: PropTypes.bool.isRequired,
  scoring: PropTypes.object,
}

Expanded.defaultProps = {
  scoring: {},
}

export default Expanded
